/**
 * Full-screen search modal with chat-based search interface.
 *
 * This component provides a modal overlay for the enhanced chat search
 * functionality, with mobile-responsive design and accessibility features.
 */

import React, { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import ChatSearch from './ChatSearch';

const SearchModal = ({ isOpen, onClose, savedSearchState, onSearchStateCleared }) => {
  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);
  const chatSearchRef = useRef(null);

  const handleNewChat = useCallback(() => {
    chatSearchRef.current?.handleNewChat();
  }, []);

  // Handle escape key and focus management
  useEffect(() => {
    if (isOpen) {
      // Store the previously focused element
      previousFocusRef.current = document.activeElement;

      // Focus the modal
      if (modalRef.current) {
        modalRef.current.focus();
      }

      // Prevent body scroll
      document.body.style.overflow = 'hidden';

      const handleEscape = (e) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };

      document.addEventListener('keydown', handleEscape);

      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = 'unset';

        // Restore focus to previous element
        if (previousFocusRef.current) {
          previousFocusRef.current.focus();
        }
      };
    }
  }, [isOpen, onClose]);

  // Don't render if not open
  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Focus trap - keep focus within modal
  const handleTabKey = (e) => {
    if (e.key !== 'Tab') return;

    const focusableElements = modalRef.current?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (!focusableElements?.length) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        lastElement.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === lastElement) {
        firstElement.focus();
        e.preventDefault();
      }
    }
  };

  const modalContent = (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="search-modal-title"
      aria-modal="true"
      role="dialog"
    >
      {/* Backdrop with playful blur */}
      <div
        className="overlay-clay"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Modal Container */}
      <div className="flex min-h-full items-center justify-center p-4 sm:p-6 lg:p-8">
        <div
          ref={modalRef}
          className="modal-clay relative w-full max-w-4xl max-h-[90vh] overflow-hidden"
          onKeyDown={handleTabKey}
          tabIndex={-1}
        >
          {/* Header */}
          <div className="flex items-center justify-between pb-4 border-b-2 border-clay-border">
            <h2
              id="search-modal-title"
              className="font-display text-xl font-bold text-gray-900 flex items-center gap-2"
            >
              <span className="text-2xl" role="img" aria-hidden="true">🔍</span>
              Search Your Articles
            </h2>
            <div className="flex items-center gap-2">
              <button
                onClick={handleNewChat}
                className="btn-clay-ghost flex items-center gap-1.5 text-sm"
                title="Start new chat"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>New</span>
              </button>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600
                  hover:bg-gray-100 rounded-clay transition-all duration-200"
                aria-label="Close search"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Chat Search Content */}
          <div className="h-[calc(90vh-120px)] sm:h-[560px] mt-4 -mx-2">
            <ChatSearch
              ref={chatSearchRef}
              onArticleClick={onClose}
              savedSearchState={savedSearchState}
              onSearchStateCleared={onSearchStateCleared}
            />
          </div>
        </div>
      </div>
    </div>
  );

  // Render modal in portal to avoid z-index issues
  return createPortal(modalContent, document.body);
};

export default SearchModal;
